import axios, { CancelTokenSource } from 'axios';
import { useContext, useRef } from 'react';
import useSWR from 'swr';
import { AXIOS_CANCEL_MSG, Customer, Page, SortDirection } from '../api/models';
import { ConfiguratorContext } from '../context';
import { SorterResult } from 'antd/lib/table/interface';

interface CustomerListProps {
  search?: string
  dealerIds?: string[]
  tags?: string[]
  current?: number
  pageSize?: number
  sorter?: SorterResult<Customer>
}

export const useCustomerList = (props: CustomerListProps) => {

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const configurator = useContext(ConfiguratorContext);

  const fetcher = async ( props:CustomerListProps ) : Promise<Page<Customer> | undefined> => {

    const { search, dealerIds, tags, current, pageSize, sorter } = props;

    const sort = [sorter].flat().map( sorter => ({
      field: sorter?.columnKey?.toString() || "name",
      direction: ( sorter?.order === 'descend' ? 'desc' : 'asc') as SortDirection,
    }));

    if ( cancelTokenSourceRef.current ) {
      cancelTokenSourceRef.current.cancel( AXIOS_CANCEL_MSG );
    }
    const cancelSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelSource;

    try {
      const resp = await configurator.api.listCustomers({
        search,
        dealerIds,
        tags,
        page: (current || 1) - 1,
        size: pageSize || 20,
        sort,
      },
        cancelSource.token,
      );
      cancelTokenSourceRef.current = undefined;

      return resp.data;
    }
    catch(e: any) {
      const id = e.response?.data?.message || e.message ;
      if ( id === AXIOS_CANCEL_MSG ) return;
      throw e;
    }
  }

  // Use SWR for data fetching
  return useSWR([
    'listCustomers',
    props
  ],
    ([_k, p]) => fetcher(p),
    {
      revalidateOnFocus: false,
      dedupingInterval:1
    }
  );
};

export default useCustomerList;
